import { useState } from 'react';
import type { ContactType, CreateContactInput } from '@accounting/shared';
import { CONTACT_TYPES } from '@accounting/shared';
import { useContacts, useCreateContact } from '../../hooks/useContacts';
import { ApiError } from '../../lib/api';

interface ContactPickerProps {
  companyId: string | null;
  value: string;
  onChange: (contactId: string) => void;
  /** Restrict the dropdown to a single contact type (e.g. 'vendor'). */
  contactType?: ContactType;
  /** Pre-select this type when adding a new contact. */
  defaultNewType?: ContactType;
  placeholder?: string;
  className?: string;
  id?: string;
}

/**
 * A contact <select> with an inline "+ New" button that opens a modal to create
 * a contact without leaving the page. On creation the new contact is selected.
 */
export function ContactPicker({
  companyId,
  value,
  onChange,
  contactType,
  defaultNewType,
  placeholder = '— select a contact —',
  className,
  id,
}: ContactPickerProps) {
  const { data: contacts = [] } = useContacts(companyId, { contactType });
  const [open, setOpen] = useState(false);

  return (
    <>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flex: 1 }}>
        <select
          id={id}
          className={className}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ flex: 1 }}
        >
          <option value="">{placeholder}</option>
          {contacts.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {contactType ? '' : ` (${c.contactType})`}
            </option>
          ))}
        </select>
        <button type="button" className="btn-secondary" onClick={() => setOpen(true)}>
          + New
        </button>
      </div>
      {open && (
        <AddContactModal
          companyId={companyId}
          defaultType={defaultNewType ?? contactType ?? 'vendor'}
          onClose={() => setOpen(false)}
          onCreated={(contactId) => {
            onChange(contactId);
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

interface AddContactModalProps {
  companyId: string | null;
  defaultType: ContactType;
  onClose: () => void;
  onCreated: (contactId: string) => void;
}

function AddContactModal({ companyId, defaultType, onClose, onCreated }: AddContactModalProps) {
  const create = useCreateContact(companyId);
  const [name, setName] = useState('');
  const [type, setType] = useState<ContactType>(defaultType);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [postalCode, setPostalCode] = useState('');

  const errorMessage =
    create.error instanceof ApiError
      ? (create.error.body as { error?: string } | null)?.error ?? 'Could not create contact'
      : create.error?.message;

  const submit = () => {
    if (!name.trim()) return;
    const input: CreateContactInput = {
      name: name.trim(),
      contactType: type,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      addressLine1: addressLine1.trim() || undefined,
      city: city.trim() || undefined,
      region: region.trim() || undefined,
      postalCode: postalCode.trim() || undefined,
    };
    create.mutate(input, { onSuccess: (contact) => onCreated(contact.id) });
  };

  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, marginTop: 10 } as const;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ fontSize: 18, marginTop: 0 }}>Add a new contact</h2>

        <label style={labelStyle} htmlFor="ac-name">Name</label>
        <input id="ac-name" value={name} autoFocus onChange={(e) => setName(e.target.value)} style={{ width: '100%' }} />

        <label style={labelStyle} htmlFor="ac-type">Type</label>
        <select id="ac-type" value={type} onChange={(e) => setType(e.target.value as ContactType)} style={{ width: '100%' }}>
          {CONTACT_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>

        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle} htmlFor="ac-email">Email</label>
            <input id="ac-email" value={email} onChange={(e) => setEmail(e.target.value)} style={{ width: '100%' }} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle} htmlFor="ac-phone">Phone</label>
            <input id="ac-phone" value={phone} onChange={(e) => setPhone(e.target.value)} style={{ width: '100%' }} />
          </div>
        </div>

        <label style={labelStyle} htmlFor="ac-addr">Address</label>
        <input id="ac-addr" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} style={{ width: '100%' }} placeholder="Street address" />
        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" style={{ flex: 2 }} />
          <input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="State" style={{ flex: 1 }} />
          <input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} placeholder="ZIP" style={{ flex: 1 }} />
        </div>

        {errorMessage && <div className="field-error" style={{ marginTop: 12 }}>{errorMessage}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={!name.trim() || create.isPending}>
            {create.isPending ? 'Saving…' : 'Add contact'}
          </button>
        </div>
      </div>
    </div>
  );
}
